import React from "react";

import styled from "styled-components";
import { IconAi } from "./IconAi";
import { TrackedButton } from "./TrackedButton";

const BORDER_WIDTH = "2px";
// ODS `small` would be 20px.
const HORIZONTAL_PADDING = { desktop: "16px", mobile: "12px" } as const;
// ODS outlined is 7px + 1px border; the border is removed, so restore 8px.
const VERTICAL_PADDING = "8px";

const ButtonWrapper = styled.div(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "right",
}));

const GradientChatButton = styled(TrackedButton)<{
  $presentation: ChatButtonProps["presentation"];
}>(({ $presentation, theme }) => {
  const { start, end } = theme.palette.loading.gradient;

  // ODS outlined hover tint, matching native `chatButton.pressedBackground`.
  const tintedState = {
    "&::after": { opacity: theme.palette.action.hoverOpacity },
  };

  // Outrank ODS `.MuiButton-outlinedPrimary` regardless of stylesheet order.
  return {
    "&&&": {
      backgroundColor: "transparent",
      border: "none",
      // Keeps the z-index -1 tint overlay inside the button.
      isolation: "isolate",
      padding: `${VERTICAL_PADDING} ${HORIZONTAL_PADDING[$presentation]}`,

      // Masked so the button stays transparent over any bar background.
      "&::before": {
        background: `linear-gradient(90deg, ${end}, ${start})`,
        borderRadius: "inherit",
        content: '""',
        inset: 0,
        mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        maskComposite: "exclude",
        padding: BORDER_WIDTH,
        pointerEvents: "none",
        position: "absolute",
        WebkitMask:
          "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        WebkitMaskComposite: "xor",
      },

      // ODS disabled border, reapplied because the ring is a pseudo-element.
      "&.Mui-disabled::before": {
        background: theme.palette.borderColor.primary,
      },

      // Cancel MUI's negative start-icon margin to keep the horizontal padding.
      "& .MuiButton-startIcon": {
        color: theme.palette.primary.main,
        marginLeft: 0,
      },

      "&::after": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "inherit",
        content: '""',
        inset: 0,
        opacity: 0,
        pointerEvents: "none",
        position: "absolute",
        zIndex: -1,
      },

      // Stops the hover tint sticking after a tap.
      "@media (hover: hover)": {
        "&:hover:not(.Mui-disabled)": tintedState,
      },
      "&:active:not(.Mui-disabled)": tintedState,
    },
  };
});

export const chatButtonMetadata = {
  dataId: "action-bar-chat-button",
  name: "chat",
  title: "Chat",
  ariaLabel: "Open chat",
} as const;

export interface ChatButtonProps {
  onClick: () => void;
  presentation: "mobile" | "desktop";
}

export const ChatButton = ({ onClick, presentation }: ChatButtonProps) => {
  const { ariaLabel, dataId, title } = chatButtonMetadata;

  return (
    <ButtonWrapper data-id={`${presentation}-chat-button`}>
      <GradientChatButton
        $presentation={presentation}
        aria-label={ariaLabel}
        color="primary"
        data-id={dataId}
        icon={<IconAi />}
        id={`${presentation}-chat-button`}
        onClick={onClick}
        size={presentation === "mobile" ? "xsmall" : "small"}
        trackingLabel={title}
        variant="outlined"
      >
        {title}
      </GradientChatButton>
    </ButtonWrapper>
  );
};
